import { useApiRequestMappers } from "@/shared/composables/use-api-request-mappers";
import { useApplicationContext } from "@/shared/composables/use-application-context";

export const useUserApi = () => {
  const { logger, httpClient } = useApplicationContext();
  const { keysToCamel, keysToSnakeCase } = useApiRequestMappers();

  const fetchUsers = async (params) => {
    try {
      let url = `/users?page=${params.page - 1}&size=${params.perPage}`;

      if (params.sort.field && params.sort.type) {
        url = `${url}&sort=${params.sort.field}&order=${params.sort.type}`
      }

      if (params.searchTerm) {
        url = `${url}&s=${params.searchTerm}`
      }

      logger.info('Getting user list');
      const response = await httpClient.get(url);
      return response.data;
    } catch (error) {
      logger.error('Failed to retrieve users', error);
      throw error;
    }
  };

  const getCurrentUserProfile = async () => {
    try {
      logger.info('Getting user profile of currently logged in user');
      const response = await httpClient.get('/users/me');
      const userProfile = keysToCamel(response.data);

      logger.debug('Retrieved current user profile', userProfile);
      return userProfile;
    } catch (error) {
      logger.error('Failed to retrieve current user profile', error);
      throw  error;
    }
  };

  const getUserProfile = async (userId) => {
    try {
      logger.info(`Getting user profile of user (${userId})`);
      const response = await httpClient.get(`/users/${userId}`);
      const userProfile = keysToCamel(response.data);

      logger.debug(`Retrieved user (${userId}) profile`, userProfile);
      return userProfile;
    } catch (error) {
      logger.error(`Failed to retrieve user (${userId}) profile`, error);
      throw  error;
    }
  };

  const updateUserProfile = async (id, data) => {
    const canUpdateRole = id !== "me";
    const requestData = keysToSnakeCase(data);
    let role = requestData.role;
    delete requestData.role;
    delete requestData.avatar;
    delete requestData.id;

    try {
      logger.info(`Update user profile of user (${id})`);
      await httpClient.put(`/users/${id}`, requestData);

      if (canUpdateRole) {
        await httpClient.put(`/users/${id}/roles`, { role });
      }
    } catch (error) {
      logger.error(`Failed to retrieve user (${id}) profile`, error);
      throw error;
    }
  };

  const deleteUserProfile = async (id) => {
    try {
      logger.info(`Delete user profile of user (${id})`);
      await httpClient.delete(`/users/${id}`);
    } catch (error) {
      logger.error(`Failed to delete user (${id}) profile`, error);
      throw error;
    }
  };

  const resetUserPassword = async (id) => {
    try {
      logger.info(`Reset user password of user (${id})`);
      await httpClient.put(`/users/${id}/reset-password`);
    } catch (error) {
      logger.error(`Failed to reset user (${id}) password`, error);
      throw error;
    }
  };

  return {
    fetchUsers,
    getUserProfile,
    getCurrentUserProfile,
    updateUserProfile,
    deleteUserProfile,
    resetUserPassword,
  };
};
